import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRevalidator } from 'react-router-dom';

import { client } from '../gql/client';
import { GET_SURVEILLANCE_ROUTES } from '../gql/queries/surveillanceRoute';
import { getCursor } from '../state/selectors/ui/tables';
import { setCursor } from '../state/actions/ui/cursorTables';
import { store } from '../state/configureStore';
import { tableIds, takeBase } from '../components/display/tables/types';
import { firstPageTake } from '../utils/cursor-take.helper';
import { adaptSurveillanceRoutesLoaderData } from '../utils/surveillanceRoute';
import { SurveillanceRoutesLoaderData } from '../pages/main/surveillanceRoute/types';
import { usePollingRevalidation } from './usePollingRevalidation';
import { SURVEILLANCE_ROUTE_LIST_REFRESHED_EVENT } from '../gql/surveillanceRouteListRefresh';

/**
 * Cursor Redux + polling + refetch compartidos por los dos listados del RV
 * (Recorregut de Vigilància): el listado activo
 * (`GeneralViewRoute/SurveillanceRoutesPage`, `tableIds.SURVEILLANCE_ROUTES`)
 * y el histórico (`SearchAndCreateHistoricalRoute`,
 * `tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES`). Extraído de
 * la duplicación ~90% detectada en la auditoría 2026-07-07 (Task 2 del
 * refactor de patrones RV) — `onReFetchData` (~60 líneas), `onPollingRefresh`
 * (~35 líneas) y `onRefetchPagination` eran copias casi idénticas entre
 * ambas páginas, con el `tableId` del cursor Redux como único parámetro real.
 *
 * ÚNICA diferencia de comportamiento encontrada entre las dos implementaciones
 * originales: el listado activo re-escribía `classification`/`track` en el
 * `setCursorData` de `onReFetchData` (eco del mismo valor ya presente en el
 * cursor). Es un no-op observable — ambos campos están en
 * `SR_HIDDEN_FILTER_IDS` (nunca se editan desde el panel de filtros), nunca
 * se envían como variable de la query GraphQL (ni aquí ni en el loader), y
 * su default en `initialCursorState` es `undefined` para los dos tableIds —
 * pero se preserva condicionando por `tableId` para no alterar el payload
 * dispatchado a Redux en ningún caso.
 */
export function useSurveillanceRoutesListing(
  rawData: SurveillanceRoutesLoaderData,
  tableId: string,
) {
  const dispatch = useDispatch();
  const revalidator = useRevalidator();

  // Adapt SR data to the Order-compatible row shape expected by the shared
  // tables (InputRadioTagTable / CheckBoxTagTable). Cada página hace su
  // propio cast final a `XTableProps['data']` — los shapes de tabla difieren
  // por tipo (single vs multi-select) aunque comparten `genericOrders`.
  const adaptedData = adaptSurveillanceRoutesLoaderData(rawData);

  const onReFetchData = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());

    await client.query({
      query: GET_SURVEILLANCE_ROUTES,
      variables: {
        // Revisión W5 (I2): la marca del turno solo la pinta el listado activo;
        // el histórico (misma query) no la pide.
        withShiftWarning: tableId === tableIds.SURVEILLANCE_ROUTES,
        input: {
          take: firstPageTake(currentCursor?.take, takeBase),
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          name: currentCursor?.routeName,
          roadId: currentCursor?.roadId,
          concessionId: currentCursor?.concessionId,
          teamId: currentCursor?.teamId,
          teamIds: currentCursor?.teamIds,
          operatorIds: currentCursor?.operatorIds,
          userIds: currentCursor?.userIds,
          // V4 (2026-04-21): `state` unificado.
          state: currentCursor?.state,
          states: currentCursor?.states,
          fromDate: currentCursor?.fromDate,
          toDate: currentCursor?.toDate,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData: Record<string, unknown> = {
      take: firstPageTake(currentCursor?.take, takeBase),
      cursor: '',
      // #74: sortField/orderBy se OMITEN (el merge del reducer los preserva).
      // Campo del CURSOR Redux = `routeName` (la variable de la query es `name`).
      routeName: currentCursor?.routeName,
      roadId: currentCursor?.roadId,
      concessionId: currentCursor?.concessionId,
      teamId: currentCursor?.teamId,
      teamIds: currentCursor?.teamIds,
      operatorIds: currentCursor?.operatorIds,
      userIds: currentCursor?.userIds,
      state: currentCursor?.state,
      states: currentCursor?.states,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      onlyFlagged: currentCursor?.onlyFlagged,
    };

    // Diferencia real preservada (ver JSDoc del hook): solo el listado
    // activo re-escribía classification/track en el cursor tras el refetch.
    if (tableId === tableIds.SURVEILLANCE_ROUTES) {
      setCursorData.classification = currentCursor?.classification;
      setCursorData.track = currentCursor?.track;
    }

    dispatch(setCursor(tableId, setCursorData));
    revalidator.revalidate();
  }, []);

  // Polling: refresca pág 1 en cache (network-only) sin tocar cursor Redux ni
  // currentServerPage, así el usuario no se mueve de la página donde esté.
  // Patrón canónico Option B (ver docs/PAGINATION-REFACTOR.md §7).
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());
    await client.query({
      query: GET_SURVEILLANCE_ROUTES,
      variables: {
        // Revisión W5 (I2): la marca del turno solo la pinta el listado activo;
        // el histórico (misma query) no la pide.
        withShiftWarning: tableId === tableIds.SURVEILLANCE_ROUTES,
        input: {
          take: firstPageTake(currentCursor?.take, takeBase),
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          name: currentCursor?.routeName,
          roadId: currentCursor?.roadId,
          concessionId: currentCursor?.concessionId,
          teamId: currentCursor?.teamId,
          teamIds: currentCursor?.teamIds,
          operatorIds: currentCursor?.operatorIds,
          userIds: currentCursor?.userIds,
          state: currentCursor?.state,
          states: currentCursor?.states,
          fromDate: currentCursor?.fromDate,
          toDate: currentCursor?.toDate,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);

  usePollingRevalidation(onPollingRefresh);

  /**
   * El listado se sirve de la caché cuando está caducada y el refresco va por
   * detrás (spec 2026-09-11). Cuando ese refresco llega, Apollo ya ha escrito
   * las filas nuevas bajo la MISMA clave, así que basta con reejecutar el
   * loader: su `readQuery` dará hit con lo nuevo y el marcador ya no estará
   * caducado, de modo que devolverá sin lanzar otro refresco. No hay bucle.
   */
  useEffect(() => {
    const onRefreshed = () => revalidator.revalidate();
    window.addEventListener(SURVEILLANCE_ROUTE_LIST_REFRESHED_EVENT, onRefreshed);
    return () =>
      window.removeEventListener(SURVEILLANCE_ROUTE_LIST_REFRESHED_EVENT, onRefreshed);
  }, [revalidator]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  return {
    adaptedData,
    onReFetchData,
    onPollingRefresh,
    onRefetchPagination,
  };
}

export default useSurveillanceRoutesListing;
